import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, eachDayOfInterval, setDate, lastDayOfMonth } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { exportElementToPDF } from "@/lib/pdfExport";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Calendar as CalendarIcon, Save, RefreshCw, History, Trash2, BookmarkPlus, FileDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

// Generate month options for the past 12 months and next 2 months
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = -12; i <= 2; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    options.push({
      value: format(date, "yyyy-MM"),
      label: format(date, "MMMM yyyy")
    });
  }
  return options;
};

export default function SalaryTracker() {
  const { teacherId } = useParams();
  const { getAuthHeader } = useAuth();
  const pageRef = useRef(null);
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const day = new Date().getDate();
    return day <= 15 ? "first" : "second";
  });
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const day = now.getDate();
    if (day <= 15) {
      return { from: setDate(now, 1), to: setDate(now, 15) };
    } else {
      return { from: setDate(now, 16), to: lastDayOfMonth(now) };
    }
  });
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editedRecords, setEditedRecords] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [addRateOpen, setAddRateOpen] = useState(false);
  const [newRateName, setNewRateName] = useState("");
  const [newRateValue, setNewRateValue] = useState("");

  // Export Payroll to PDF - captures the exact UI
  const handleExportPDF = async () => {
    if (!pageRef.current) return;
    try {
      toast.info("Generating PDF...");
      const startDate = format(dateRange.from, "yyyy-MM-dd");
      const endDate = format(dateRange.to, "yyyy-MM-dd");
      await exportElementToPDF(
        pageRef.current,
        `payroll_${teacher?.name || 'teacher'}_${startDate}_to_${endDate}.pdf`,
        { orientation: "landscape" }
      );
      toast.success("Payroll PDF downloaded!");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  // Update date range when month or period changes
  const updateDateRange = useCallback((month, period) => {
    const [year, monthNum] = month.split("-").map(Number);
    const baseDate = new Date(year, monthNum - 1, 1);
    
    if (period === "first") {
      setDateRange({
        from: setDate(baseDate, 1),
        to: setDate(baseDate, 15)
      });
    } else {
      setDateRange({
        from: setDate(baseDate, 16),
        to: lastDayOfMonth(baseDate)
      });
    }
  }, []);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    updateDateRange(month, selectedPeriod);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    updateDateRange(selectedMonth, period);
  };

  const fetchTeacher = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teachers/${teacherId}`);
      setTeacher(response.data);
    } catch (error) {
      console.error("Failed to fetch teacher:", error);
      toast.error("Failed to load teacher");
    }
  }, [teacherId]);

  const fetchSalaryData = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) return;
    
    setLoading(true);
    try {
      const startDate = format(dateRange.from, "yyyy-MM-dd");
      const endDate = format(dateRange.to, "yyyy-MM-dd");
      
      const response = await axios.get(
        `${API}/teachers/${teacherId}/salary-records?start_date=${startDate}&end_date=${endDate}`
      );
      
      // Create a map of existing records by date
      const recordMap = {};
      response.data.records.forEach(r => {
        recordMap[r.date] = r;
      });
      
      // Generate all dates in range with records (existing or empty)
      const allDates = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
      const allRecords = allDates.map(date => {
        const dateStr = format(date, "yyyy-MM-dd");
        return recordMap[dateStr] || {
          date: dateStr,
          classes_25min: 0,
          classes_30min: 0,
          demo_bonus: 0,
          teacher_status: 'present',
          additional_classes: {},
          isNew: true
        };
      });
      
      setRecords(allRecords);
      setSummary(response.data.summary);
      setEditedRecords({});
    } catch (error) {
      console.error("Failed to fetch salary data:", error);
      toast.error("Failed to load salary data");
    } finally {
      setLoading(false);
    }
  }, [teacherId, dateRange]);

  const fetchPayrollHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/teachers/${teacherId}/payroll-history`);
      setPayrollHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch payroll history:", error);
    }
  }, [teacherId]);

  useEffect(() => {
    fetchTeacher();
  }, [fetchTeacher]);

  useEffect(() => {
    if (teacher) {
      fetchSalaryData();
      fetchPayrollHistory();
    }
  }, [teacher, fetchSalaryData, fetchPayrollHistory]);

  const handleInputChange = (date, field, value) => {
    if (field === 'teacher_status') {
      setEditedRecords(prev => ({
        ...prev,
        [date]: {
          ...(prev[date] || records.find(r => r.date === date) || {}),
          date,
          [field]: value
        }
      }));
      return;
    }
    const numValue = field === 'demo_bonus' 
      ? Math.max(0, parseFloat(value) || 0)
      : Math.max(0, parseInt(value) || 0);
    setEditedRecords(prev => ({
      ...prev,
      [date]: {
        ...(prev[date] || records.find(r => r.date === date) || {}),
        date,
        [field]: numValue
      }
    }));
  };

  // Handle additional rate class count changes
  const handleAdditionalClassChange = (date, rateName, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    setEditedRecords(prev => {
      const existing = prev[date] || records.find(r => r.date === date) || {};
      const existingAdditional = existing.additional_classes || {};
      return {
        ...prev,
        [date]: {
          ...existing,
          date,
          additional_classes: {
            ...existingAdditional,
            [rateName]: numValue
          }
        }
      };
    });
  };

  const getRecordValue = (date, field) => {
    if (editedRecords[date] && editedRecords[date][field] !== undefined) {
      return editedRecords[date][field];
    }
    const record = records.find(r => r.date === date);
    if (field === 'teacher_status') {
      return record ? (record[field] || 'present') : 'present';
    }
    return record ? (record[field] || 0) : 0;
  };

  const getAdditionalClassValue = (date, rateName) => {
    if (editedRecords[date]?.additional_classes?.[rateName] !== undefined) {
      return editedRecords[date].additional_classes[rateName];
    }
    const record = records.find(r => r.date === date);
    return record?.additional_classes?.[rateName] || 0;
  };

  const hasChanges = Object.keys(editedRecords).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      // Send ONLY fields the admin actually changed, so auto-synced class counts are never overwritten with stale values
      const fields = ['classes_25min', 'classes_30min', 'demo_bonus', 'teacher_status'];
      const recordsToSave = Object.values(editedRecords).map(r => {
        const existingRecord = records.find(rec => rec.date === r.date);
        const payload = { date: r.date };
        fields.forEach(f => {
          if (r[f] === undefined) return;
          const original = existingRecord ? existingRecord[f] : undefined;
          if (!existingRecord || r[f] !== original) payload[f] = r[f];
        });
        const changedAdditional = {};
        Object.entries(r.additional_classes || {}).forEach(([name, val]) => {
          const original = existingRecord?.additional_classes?.[name];
          if (!existingRecord || val !== original) changedAdditional[name] = val;
        });
        if (Object.keys(changedAdditional).length) payload.additional_classes = changedAdditional;
        return payload;
      }).filter(p => Object.keys(p).length > 1);
      
      await axios.post(`${API}/teachers/${teacherId}/salary-records/bulk`, recordsToSave);
      toast.success("Salary records saved");
      fetchSalaryData();
    } catch (error) {
      console.error("Failed to save records:", error);
      toast.error("Failed to save records");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveToHistory = async () => {
    try {
      const startDate = format(dateRange.from, "yyyy-MM-dd");
      const endDate = format(dateRange.to, "yyyy-MM-dd");
      await axios.post(`${API}/teachers/${teacherId}/payroll-history?start_date=${startDate}&end_date=${endDate}`);
      toast.success("Payroll saved to history");
      fetchPayrollHistory();
    } catch (error) {
      console.error("Failed to save to history:", error);
      toast.error("Failed to save to history");
    }
  };

  const handleDeleteHistory = async (historyId) => {
    try {
      await axios.delete(`${API}/teachers/${teacherId}/payroll-history/${historyId}`);
      toast.success("Payroll history deleted");
      fetchPayrollHistory();
    } catch (error) {
      console.error("Failed to delete history:", error);
      toast.error("Failed to delete history");
    }
  };

  // Add new rate row to teacher
  const handleAddRate = async () => {
    if (!newRateName.trim() || !newRateValue) {
      toast.error("Please enter a rate name and value");
      return;
    }
    const rate = parseFloat(newRateValue);
    if (isNaN(rate) || rate < 0) {
      toast.error("Please enter a valid rate");
      return;
    }
    // Check duplicate name
    const existingRates = teacher.additional_rates || [];
    if (existingRates.some(r => r.name === newRateName.trim())) {
      toast.error("A rate with this name already exists");
      return;
    }
    try {
      const updatedRates = [...existingRates, { name: newRateName.trim(), rate }];
      await axios.put(`${API}/teachers/${teacherId}`, { additional_rates: updatedRates });
      toast.success(`Rate "${newRateName.trim()}" added`);
      setAddRateOpen(false);
      setNewRateName("");
      setNewRateValue("");
      fetchTeacher();
      fetchSalaryData();
    } catch (error) {
      toast.error("Failed to add rate");
    }
  };

  // Remove a custom rate
  const handleRemoveRate = async (rateName) => {
    if (!window.confirm(`Remove rate "${rateName}"? This won't delete existing records.`)) return;
    const updatedRates = (teacher.additional_rates || []).filter(r => r.name !== rateName);
    try {
      await axios.put(`${API}/teachers/${teacherId}`, { additional_rates: updatedRates });
      toast.success(`Rate "${rateName}" removed`);
      fetchTeacher();
      fetchSalaryData();
    } catch (error) {
      toast.error("Failed to remove rate");
    }
  };

  const calculateDailyTotal = (date) => {
    const c25 = getRecordValue(date, 'classes_25min');
    const c30 = getRecordValue(date, 'classes_30min');
    const bonus = getRecordValue(date, 'demo_bonus');
    if (!teacher) return 0;
    let total = (c25 * teacher.rate_25min) + (c30 * teacher.rate_30min) + bonus;
    // Add additional rates
    (teacher.additional_rates || []).forEach(r => {
      total += getAdditionalClassValue(date, r.name) * r.rate;
    });
    return total;
  };

  const calculateLiveTotal = () => {
    if (!teacher) return { total25: 0, total30: 0, totalBonus: 0, pay25: 0, pay30: 0, additionalPay: 0, grandTotal: 0, additionalTotals: {} };
    
    let total25 = 0, total30 = 0, totalBonus = 0;
    const additionalTotals = {};
    
    (teacher.additional_rates || []).forEach(r => {
      additionalTotals[r.name] = { count: 0, rate: r.rate, pay: 0 };
    });
    
    records.forEach(r => {
      total25 += getRecordValue(r.date, 'classes_25min');
      total30 += getRecordValue(r.date, 'classes_30min');
      totalBonus += getRecordValue(r.date, 'demo_bonus');
      (teacher.additional_rates || []).forEach(rate => {
        additionalTotals[rate.name].count += getAdditionalClassValue(r.date, rate.name);
      });
    });
    
    const pay25 = total25 * teacher.rate_25min;
    const pay30 = total30 * teacher.rate_30min;
    let additionalPay = 0;
    Object.keys(additionalTotals).forEach(name => {
      additionalTotals[name].pay = additionalTotals[name].count * additionalTotals[name].rate;
      additionalPay += additionalTotals[name].pay;
    });
    
    return {
      total25,
      total30,
      totalBonus,
      pay25,
      pay30,
      additionalPay,
      additionalTotals,
      grandTotal: pay25 + pay30 + totalBonus + additionalPay
    };
  };

  if (loading && !teacher) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="salary-tracker-loading">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="text-center py-12" data-testid="teacher-not-found">
        <p className="text-slate-500">Teacher not found</p>
        <Link to="/teachers" className="text-indigo-600 hover:underline mt-2 inline-block">
          Back to Teachers
        </Link>
      </div>
    );
  }

  const liveTotal = calculateLiveTotal();
  const additionalRates = teacher.additional_rates || [];
  // Total number of columns: Date + Status + 25min + 30min + (additionalRates) + Demo Bonus + Daily Total
  const totalColumns = 6 + additionalRates.length;

  return (
    <div className="space-y-6" data-testid="salary-tracker-page" ref={pageRef}>
      {/* Back button */}
      <Link
        to="/teachers"
        className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        data-testid="back-to-teachers"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Teachers
      </Link>

      {/* Header */}
      <Card className="border-slate-200 shadow-sm bg-gradient-to-r from-teal-50 to-cyan-50" data-testid="salary-header-card">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-teal-700 font-bold text-xl">
                  {teacher.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" data-testid="teacher-name">
                  {teacher.name} - Payslip
                </h1>
                <p className="text-slate-600">
                  Rates: ₱{teacher.rate_25min}/25min • ₱{teacher.rate_30min}/30min
                  {additionalRates.map(r => ` • ₱${r.rate}/${r.name}`).join('')}
                </p>
              </div>
            </div>
            
            {/* Payroll Period Selector */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Month Selector */}
              <Select value={selectedMonth} onValueChange={handleMonthChange}>
                <SelectTrigger className="w-[160px]" data-testid="month-selector">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Period Toggle */}
              <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="period-toggle">
                <button
                  onClick={() => handlePeriodChange("first")}
                  className={cn(
                    "px-4 py-2 text-sm font-medium transition-colors",
                    selectedPeriod === "first"
                      ? "bg-teal-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  )}
                  data-testid="period-first-half"
                >
                  1st - 15th
                </button>
                <button
                  onClick={() => handlePeriodChange("second")}
                  className={cn(
                    "px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200",
                    selectedPeriod === "second"
                      ? "bg-teal-600 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-50"
                  )}
                  data-testid="period-second-half"
                >
                  16th - End
                </button>
              </div>

              {/* Custom Date Range (optional) */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-slate-500"
                    data-testid="custom-date-picker"
                  >
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    Custom
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from && range?.to) {
                        setDateRange(range);
                      } else if (range?.from) {
                        setDateRange({ from: range.from, to: range.from });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>

              <Button
                variant="outline"
                size="icon"
                onClick={fetchSalaryData}
                disabled={loading}
                data-testid="refresh-btn"
              >
                <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
              </Button>
            </div>
          </div>

          {/* Date Range Display & Actions */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              Showing: <span className="font-medium text-slate-700">
                {format(dateRange.from, "MMMM d")} - {format(dateRange.to, "MMMM d, yyyy")}
              </span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
                data-testid="view-history-btn"
              >
                <History className="w-4 h-4 mr-1" />
                History
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveToHistory}
                data-testid="save-to-history-btn"
              >
                <BookmarkPlus className="w-4 h-4 mr-1" />
                Save Period
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-1" />
                Export PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card - Cut-Off Period Total */}
      <Card className="border-slate-200 shadow-sm" data-testid="summary-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Cut-Off Summary: {format(dateRange.from, "MMM d")} – {format(dateRange.to, "MMM d, yyyy")}
            </h3>
            <div className="bg-teal-600 text-white px-5 py-3 rounded-xl shadow-md" data-testid="amount-to-release">
              <p className="text-xs uppercase tracking-wide opacity-80">Amount to Release</p>
              <p className="text-2xl font-bold">₱{liveTotal.grandTotal.toLocaleString()}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">25-min Classes</p>
              <p className="text-2xl font-bold text-slate-900">{liveTotal.total25}</p>
              <p className="text-sm text-slate-600">x ₱{teacher.rate_25min} = ₱{liveTotal.pay25.toLocaleString()}</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">30-min Classes</p>
              <p className="text-2xl font-bold text-slate-900">{liveTotal.total30}</p>
              <p className="text-sm text-slate-600">x ₱{teacher.rate_30min} = ₱{liveTotal.pay30.toLocaleString()}</p>
            </div>
            {additionalRates.map(r => {
              const data = liveTotal.additionalTotals[r.name] || { count: 0, pay: 0 };
              return (
                <div key={r.name} className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-700">{r.name}</p>
                  <p className="text-2xl font-bold text-blue-600">{data.count}</p>
                  <p className="text-sm text-blue-600">x ₱{r.rate} = ₱{data.pay.toLocaleString()}</p>
                </div>
              );
            })}
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-sm text-amber-700">Demo Bonus</p>
              <p className="text-2xl font-bold text-amber-600">₱{liveTotal.totalBonus.toLocaleString()}</p>
              <p className="text-sm text-amber-600">Extra</p>
            </div>
            <div className="text-center p-4 bg-teal-50 rounded-lg border border-teal-100">
              <p className="text-sm text-teal-700">Total Earnings</p>
              <p className="text-3xl font-bold text-teal-600" data-testid="total-earnings">
                ₱{liveTotal.grandTotal.toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Class Records Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-lg font-semibold">Daily Class Records</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddRateOpen(true)}
              data-testid="add-rate-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Rate Row
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={cn(
                "btn-active",
                hasChanges ? "bg-teal-600 hover:bg-teal-700" : "bg-slate-300"
              )}
              data-testid="save-records-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold text-slate-600 w-32">Date</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-center w-36">
                    Status
                    <span className="block text-xs font-normal text-slate-400">Teacher</span>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-600 text-center w-32">
                    25-min
                    <span className="block text-xs font-normal text-slate-400">₱{teacher.rate_25min}</span>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-600 text-center w-32">
                    30-min
                    <span className="block text-xs font-normal text-slate-400">₱{teacher.rate_30min}</span>
                  </TableHead>
                  {additionalRates.map(r => (
                    <TableHead key={r.name} className="font-semibold text-blue-600 text-center w-32">
                      <div className="flex items-center justify-center gap-1">
                        <span>{r.name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveRate(r.name); }}
                          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer"
                          title={`Remove ${r.name}`}
                          data-testid={`remove-rate-${r.name.replace(/\s+/g, '-')}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="block text-xs font-normal text-blue-400">₱{r.rate}</span>
                    </TableHead>
                  ))}
                  <TableHead className="font-semibold text-slate-600 text-center w-32">
                    Demo Bonus
                    <span className="block text-xs font-normal text-amber-500">₱</span>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right w-32">Daily Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={totalColumns} className="text-center py-12 text-slate-400">
                      Loading records...
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={totalColumns} className="text-center py-12 text-slate-500">
                      Select a date range to view records
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => {
                    const dailyTotal = calculateDailyTotal(record.date);
                    const isEdited = editedRecords[record.date];
                    const hasBonus = getRecordValue(record.date, 'demo_bonus') > 0;
                    return (
                      <TableRow
                        key={record.date}
                        className={cn(
                          "table-row-hover",
                          isEdited && "bg-amber-50",
                          hasBonus && !isEdited && "bg-amber-50/30"
                        )}
                        data-testid={`record-row-${record.date}`}
                      >
                        <TableCell className="font-medium text-slate-900">
                          {format(parseISO(record.date), "MM/dd/yyyy")}
                        </TableCell>
                        <TableCell className="text-center">
                          <Select
                            value={getRecordValue(record.date, 'teacher_status')}
                            onValueChange={(val) => handleInputChange(record.date, 'teacher_status', val)}
                          >
                            <SelectTrigger
                              className={cn(
                                "w-28 h-8 text-xs mx-auto",
                                getRecordValue(record.date, 'teacher_status') === 'absent' && "bg-red-50 border-red-200 text-red-700",
                                getRecordValue(record.date, 'teacher_status') === 'late' && "bg-amber-50 border-amber-200 text-amber-700",
                                getRecordValue(record.date, 'teacher_status') === 'present' && "bg-green-50 border-green-200 text-green-700"
                              )}
                              data-testid={`input-status-${record.date}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="present">Present</SelectItem>
                              <SelectItem value="late">Late</SelectItem>
                              <SelectItem value="absent">Absent</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            value={getRecordValue(record.date, 'classes_25min')}
                            onChange={(e) => handleInputChange(record.date, 'classes_25min', e.target.value)}
                            className="w-20 h-10 text-center text-base font-semibold text-slate-900 mx-auto"
                            data-testid={`input-25min-${record.date}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            value={getRecordValue(record.date, 'classes_30min')}
                            onChange={(e) => handleInputChange(record.date, 'classes_30min', e.target.value)}
                            className="w-20 h-10 text-center text-base font-semibold text-slate-900 mx-auto"
                            data-testid={`input-30min-${record.date}`}
                          />
                        </TableCell>
                        {additionalRates.map(r => (
                          <TableCell key={r.name} className="text-center">
                            <Input
                              type="number"
                              min="0"
                              value={getAdditionalClassValue(record.date, r.name)}
                              onChange={(e) => handleAdditionalClassChange(record.date, r.name, e.target.value)}
                              className="w-20 h-10 text-center text-base font-semibold text-blue-800 mx-auto bg-blue-50/50 border-blue-200"
                              data-testid={`input-${r.name.replace(/\s+/g, '-')}-${record.date}`}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={getRecordValue(record.date, 'demo_bonus')}
                            onChange={(e) => handleInputChange(record.date, 'demo_bonus', e.target.value)}
                            className="w-24 h-10 text-center text-base font-semibold text-amber-800 mx-auto bg-amber-50 border-amber-200"
                            data-testid={`input-bonus-${record.date}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium text-slate-900">
                          {dailyTotal > 0 ? `₱${dailyTotal.toLocaleString()}` : "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Footer Summary */}
      {records.length > 0 && (
        <Card className="border-teal-200 shadow-sm bg-teal-50" data-testid="footer-summary">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-slate-700 space-y-1">
                <div>
                  <span className="font-medium">{liveTotal.total25}</span> x ₱{teacher.rate_25min} = ₱{liveTotal.pay25.toLocaleString()}
                  <span className="mx-3">+</span>
                  <span className="font-medium">{liveTotal.total30}</span> x ₱{teacher.rate_30min} = ₱{liveTotal.pay30.toLocaleString()}
                </div>
                {additionalRates.map(r => {
                  const data = liveTotal.additionalTotals[r.name] || { count: 0, pay: 0 };
                  return data.count > 0 ? (
                    <div key={r.name} className="text-blue-700">
                      + {r.name}: <span className="font-medium">{data.count}</span> x ₱{r.rate} = ₱{data.pay.toLocaleString()}
                    </div>
                  ) : null;
                })}
                {liveTotal.totalBonus > 0 && (
                  <div className="text-amber-700">
                    + Demo Bonus: ₱{liveTotal.totalBonus.toLocaleString()}
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-teal-700">Total Earnings</p>
                <p className="text-3xl font-bold text-teal-700" data-testid="footer-total">
                  ₱{liveTotal.grandTotal.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payroll History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-2xl" aria-describedby="history-description">
          <DialogHeader>
            <DialogTitle>Payroll History - {teacher.name}</DialogTitle>
            <p id="history-description" className="text-sm text-slate-500">
              View and manage saved payroll periods
            </p>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {payrollHistory.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No payroll history saved yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-center">Classes</TableHead>
                    <TableHead className="text-center">Bonus</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollHistory.map((h) => (
                    <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                      <TableCell className="font-medium">
                        {h.period_start} to {h.period_end}
                      </TableCell>
                      <TableCell className="text-center text-slate-600">
                        {h.total_25min_classes + h.total_30min_classes}
                      </TableCell>
                      <TableCell className="text-center text-amber-600">
                        ₱{(h.total_demo_bonus || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-teal-600">
                        ₱{h.total_earnings.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteHistory(h.id)}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`delete-history-${h.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Rate Row Dialog */}
      <Dialog open={addRateOpen} onOpenChange={setAddRateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-rate-dialog" aria-describedby="add-rate-description">
          <DialogHeader>
            <DialogTitle>Add New Rate Row</DialogTitle>
            <p id="add-rate-description" className="text-sm text-slate-500">
              Add a custom payroll rate category for {teacher.name}
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rate-name">Rate Name</Label>
              <Input
                id="rate-name"
                value={newRateName}
                onChange={(e) => setNewRateName(e.target.value)}
                placeholder="e.g., Group Class, 45-min, etc."
                data-testid="new-rate-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate-value">Rate per Class (₱)</Label>
              <Input
                id="rate-value"
                type="number"
                min="0"
                step="0.01"
                value={newRateValue}
                onChange={(e) => setNewRateValue(e.target.value)}
                placeholder="e.g., 60"
                data-testid="new-rate-value-input"
              />
            </div>
            {newRateName && newRateValue && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                New column: <strong>{newRateName}</strong> at ₱{newRateValue} per class
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRateOpen(false)} data-testid="cancel-add-rate-btn">
              Cancel
            </Button>
            <Button
              onClick={handleAddRate}
              disabled={!newRateName.trim() || !newRateValue}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-add-rate-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
